import React, { useState, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import { useShowEligibility } from '@/v2/hooks/insurance/useEligibilities'
import { ContentWrapper, AccordionStyled } from './show-lists/styles'
import ActiveCoverage from './show-lists/ActiveCoverage'
import EligibilityItem from './show-lists/EligibilityItem'
import PatientData from './show-lists/PatientData'
import SubscriberData from './show-lists/SubscriberData'
import HealthPlan from './show-lists/HealthPlan'
import AlertMessage from './show-lists/AlertMessage'
import Grid from '@mui/material/Grid'
import Button from '@/v2/components/common/Button'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Skeleton from '@mui/material/Skeleton'
import CodeBlock from '@/v2/components/common/CodeBlock'
import Box from '@mui/material/Box'

const Show = ({ eligibility, onProceed }) => {
  const theme = useTheme()
  const [activeCoverage, setActiveCoverage] = useState(null)
  const [activeCoverageEnhanced, setActiveCoverageEnhanced] = useState([])
  const [expandedPanel, setExpandedPanel] = useState('patientData')
  const [coInsurances, setCoInsurances] = useState([])
  const [deductibles, setDeductibles] = useState([])
  const [limitations, setLimitations] = useState([])
  const [patientData, setPatientData] = useState(null)
  const [subscriberData, setSubscriberData] = useState(null)
  const [healthPlan, setHealthPlan] = useState(null)
  const [errors, setErrors] = useState(null)
  const { eligibilityInfo, isLoading, isError } = useShowEligibility(
    eligibility?.insurance?.id,
    eligibility?.id
  )
  const navigate = useNavigate()

  const loaderComponent = () => (
    <ContentWrapper>
      <Grid xs={12} item mb="24px">
        <Button
          key={name}
          variant="contained"
          disableElevation
          sx={{
            background: statusColors[eligibilityInfo?.status],
            color: theme.palette.background.default,
            width: 'auto',
            minWidth: '40px',
            height: '20px',
            mt: '15px',
            cursor: 'not-allowed',
            pointerEvents: 'none',
          }}
        >
          {eligibilityInfo?.status}
        </Button>
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="80%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="70%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="75%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="80%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="82%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="70%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="80%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="70%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="75%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="80%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="82%" />
      </Grid>
      <Grid xs={12} item mb="24px">
        <Skeleton variant="rectangular" width="70%" />
      </Grid>
    </ContentWrapper>
  )

  const statusColors = {
    failed: theme.palette.error.main,
    created: theme.palette.success.main,
    requested_with_error: theme.palette.error.main,
    requested: theme.palette.warning.main,
  }

  function formatBackendMessage(message) {
    if (!message) return ''
    const regex = /^([\w.]+): Invalid type\. Expected: ([\w]+), given: ([\w]+)$/
    const match = message?.match(regex)
    if (match) {
      const [_, field, expectedType, givenType] = match
      const fieldMapping = {
        'patient.dateOfBirth': 'Date of Birth',
      }
      const userFriendlyField = fieldMapping[field] || field
      return `${userFriendlyField} is invalid. Expected a ${expectedType}, but received ${givenType}.`
    }
    return message
  }

  useEffect(() => {
    if (eligibilityInfo?.response) {
      const {
        coInsurances,
        deductibles,
        limitationAndMaximum,
        activeCoverage,
        activeCoverageEnhanced,
        patient,
        subscriber,
      } = eligibilityInfo.response
      const healthObject = {
        insurance: eligibilityInfo?.insurance,
        plan: patient?.plan,
      }
      const [activeCoverageData] = activeCoverage || []
      const errors = eligibilityInfo?.response?.errors || []

      if (eligibilityInfo?.status === 'requested' && !errors?.length) {
        onProceed(true)
      } else {
        onProceed(false)
      }

      setLimitations(limitationAndMaximum)
      setDeductibles(deductibles)
      setCoInsurances(coInsurances)
      setActiveCoverage(activeCoverageData)
      setActiveCoverageEnhanced(activeCoverageEnhanced || [])
      setPatientData(patient)
      setSubscriberData(subscriber)
      setHealthPlan(healthObject)
      setErrors(errors)
    }
  }, [eligibilityInfo])

  if (isLoading && !isError) return loaderComponent()

  if (isError)
    return (
      <ContentWrapper>
        <Typography
          variant="h5"
          fontWeight="300"
          sx={{ textAlign: 'center', marginTop: '40px' }}
        >
          It wasn't possible to load the eligibility.
        </Typography>
      </ContentWrapper>
    )

  if (
    eligibilityInfo?.status === 'requesting' ||
    eligibilityInfo?.status === 'created'
  ) {
    return loaderComponent()
  }

  if (!!errors?.length)
    return (
      <ContentWrapper>
        <Box mr={6}>
          <Typography variant="body2" color="error" fontWeight="bold">
            Errors:
          </Typography>
          {errors.map((error, index) => (
            <Box mb={1} key={index}>
              {error?.errorMessage && (
                <CodeBlock>
                  {formatBackendMessage(error?.errorMessage)}
                </CodeBlock>
              )}
            </Box>
          ))}
        </Box>
        <Box mt={1} mr={6}>
          <Box mb={1}>
            <CodeBlock>
              Code: {eligibilityInfo?.response?.status?.code}
            </CodeBlock>
          </Box>
        </Box>

        <Box mt={1} mr={6}>
          <Box mb={1}>
            <CodeBlock>
              Response: {eligibilityInfo?.response?.status?.description}
            </CodeBlock>
          </Box>
        </Box>
      </ContentWrapper>
    )

  return (
    <ContentWrapper>
      <Grid xs={12} item mb="24px">
        <Button
          key={name}
          variant="contained"
          disableElevation
          sx={{
            background: statusColors[eligibilityInfo?.status],
            color: theme.palette.background.default,
            width: 'auto',
            minWidth: '40px',
            height: '20px',
            mt: '15px',
            cursor: 'not-allowed',
            pointerEvents: 'none',
            ml: '0px',
          }}
        >
          {eligibilityInfo?.status === 'requested_with_error'
            ? 'Failed'
            : eligibilityInfo?.status}
        </Button>
      </Grid>
      {activeCoverageEnhanced && activeCoverageEnhanced.length > 0 && (
        <Grid xs={12} item mb="24px">
          <AlertMessage activeCoverageEnhanced={activeCoverageEnhanced} />
        </Grid>
      )}
      {eligibilityInfo?.response?.payerStatus && (
        <Grid xs={12} item mb="24px">
          <Typography variant="h4" fontWeight={500}>
            Payer Status:{' '}
            <Typography variant="span" fontWeight={200}>
              {eligibilityInfo?.response?.payerStatus}
            </Typography>
          </Typography>
        </Grid>
      )}
      {patientData && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'patientData'}
          noBackground
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'patientData' ? null : 'patientData'
            )
          }
          title={<Typography>Patient</Typography>}
          content={
            <PatientData
              patient={patientData}
              existentPatientData={eligibilityInfo?.patient}
              insuranceId={eligibilityInfo?.insurance?.id}
              eligibilityId={eligibilityInfo?.id}
            />
          }
        />
      )}

      {subscriberData && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'subscriberData'}
          noBackground
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'subscriberData' ? null : 'subscriberData'
            )
          }
          title={<Typography>Subscriber</Typography>}
          content={<SubscriberData subscriber={subscriberData} />}
        />
      )}

      {healthPlan && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'healthplan'}
          noBackground
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'healthplan' ? null : 'healthplan'
            )
          }
          title={<Typography>Insurance & Plan</Typography>}
          content={<HealthPlan healthplan={healthPlan} />}
        />
      )}

      {activeCoverage && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'activeCoverage'}
          noBackground
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'activeCoverage' ? null : 'activeCoverage'
            )
          }
          title={<Typography>Active Coverage</Typography>}
          content={<ActiveCoverage activeCoverage={[activeCoverage]} />}
        />
      )}

      {coInsurances && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'coInsurances'}
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'coInsurances' ? null : 'coInsurances'
            )
          }
          title={<Typography>Co-Insurance</Typography>}
          content={
            <EligibilityItem
              data={coInsurances}
              searchLabel="Search Co-Insurance"
            />
          }
        />
      )}

      {deductibles && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'deductibles'}
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'deductibles' ? null : 'deductibles'
            )
          }
          title={<Typography>Deductible</Typography>}
          content={
            <EligibilityItem
              data={deductibles}
              searchLabel="Search Deductible"
            />
          }
        />
      )}
      {limitations && (
        <AccordionStyled
          noPaddingTitle
          expanded={expandedPanel === 'limitations'}
          onChange={() =>
            setExpandedPanel(
              expandedPanel === 'limitations' ? null : 'limitations'
            )
          }
          title={<Typography>Limitation and Maximum</Typography>}
          content={
            <EligibilityItem
              data={limitations}
              type="Search Limitation and Maximum"
            />
          }
        />
      )}
    </ContentWrapper>
  )
}

export default Show
